import { useMutation, useQueryClient } from 'react-query'
import { useAuth0 } from '../../../../../contexts/auth0-context'
import callAPI from '../../../../../API/callSecureAPI'
import endpoints from '../../../../../API/endpoints'
import { TppCell, TppRow, TppSlot } from '../../../../../components/TppView/types'
import { useInvalidateTPPChangeLogQuery } from '../../../../../components/TPPChangeLog/queries/useInvalidateTPPChangeLogQuery'

type UpdateCellContentPayload = {
    cellId: string
    tppId: string
    content: string
}

export function useUpdateTPPCellContentMutation() {
    const { getTokenSilently } = useAuth0()
    const queryClient = useQueryClient()
    const { invalidateTPPChangeLogQuery } = useInvalidateTPPChangeLogQuery()

    const updateContent = async (payload: UpdateCellContentPayload) => {
        const token = await getTokenSilently({ audience: 'https://atlas.aesara.com' })

        const { url, method } = endpoints.updateTPPCellContent

        // BE route: router.put('/tpps/cells/:cellId/update', ...)
        const res = await callAPI(`${url}/${payload.cellId}/update`, method, token, {
            content: payload.content,
        })

        // backend returns { success: true, data: result }
        return res.data
    }

    return useMutation<
        TppCell,
        unknown,
        { cellId: string; tppId: string; content: string },
        {
            previousCells: TppCell[] | undefined
        }
    >(updateContent, {
        onMutate: async ({ cellId, tppId, content }) => {
            await queryClient.cancelQueries(['tppCells', tppId])
            const previousCells = queryClient.getQueryData<TppCell[]>(['tppCells', tppId])
            if (previousCells) {
                queryClient.setQueryData<TppCell[]>(
                    ['tppCells', tppId],
                    previousCells.map((c) => (c._id === cellId ? { ...c, content } : c))
                )
            }
            return { previousCells }
        },
        onError: (_err, variables, context) => {
            if (context?.previousCells) {
                queryClient.setQueryData(['tppCells', variables.tppId], context.previousCells)
            }
        },
        // keep server as source of truth
        onSuccess: (data, vars) => {
            const updatedCell = data
            if (!updatedCell) return
            queryClient.setQueryData<TppCell[]>(['tppCells', vars.tppId], (old) => {
                if (!old) return []
                return old.map((c) => (c._id === updatedCell._id ? { ...c, ...updatedCell } : c))
            })
            invalidateTPPChangeLogQuery(vars.tppId)
        },
    })
}
